<template>
    <div class="meta-insights">
        <div class="header">
            <div class="title">{{ $t('whatsapp_business_api.metaInsightsTitle') }}</div>
            <div class="subtitle">{{ $t('whatsapp_business_api.metaInsightsSubtitle') }}</div>
        </div>

        <div class="filters">
            <div class="filter-item">
                <label>{{ $t('whatsapp_business_api.metaInsightsDateRange') }}</label>
                <a-range-picker
                    v-model="dateRange"
                    :format="dateFormat"
                    :placeholder="[$t('whatsapp_business_api.metaInsightsDateStart'), $t('whatsapp_business_api.metaInsightsDateEnd')]"
                    @change="onDateChange"
                />
            </div>
            <div class="filter-item">
                <label>{{ $t('whatsapp_business_api.metaInsightsGranularity') }}</label>
                <a-select v-model="granularity" style="width: 150px" @change="onGranularityChange">
                    <a-select-option value="DAY">{{ $t('whatsapp_business_api.metaInsightsGranularityDay') }}</a-select-option>
                    <a-select-option value="MONTH">{{ $t('whatsapp_business_api.metaInsightsGranularityMonth') }}</a-select-option>
                    <a-select-option value="HALF_HOUR">{{ $t('whatsapp_business_api.metaInsightsGranularityHalfHour') }}</a-select-option>
                </a-select>
            </div>
            <div class="filter-item">
                <a-button type="primary" @click="fetchAllData" :loading="loading">
                    <span class="material-icons" style="font-size: 16px; margin-right: 4px">refresh</span>
                    {{ $t('whatsapp_business_api.metaInsightsRefresh') }}
                </a-button>
            </div>
        </div>

        <a-tabs v-model="activeTab" @change="onTabChange">
            <a-tab-pane key="messaging" :tab="$t('whatsapp_business_api.metaInsightsTabMessaging')">
                <MessagingAnalytics
                    :channel="channel"
                    :filters="filters"
                    :loading="loadingStates.messaging"
                    :data="analyticsData.messaging"
                />
            </a-tab-pane>
            <a-tab-pane key="conversation" :tab="$t('whatsapp_business_api.metaInsightsTabConversation')">
                <ConversationAnalytics
                    :channel="channel"
                    :filters="filters"
                    :loading="loadingStates.conversation"
                    :data="analyticsData.conversation"
                />
            </a-tab-pane>
            <a-tab-pane key="pricing" :tab="$t('whatsapp_business_api.metaInsightsTabPricing')">
                <PricingAnalytics
                    :channel="channel"
                    :filters="filters"
                    :loading="loadingStates.pricing"
                    :data="analyticsData.pricing"
                />
            </a-tab-pane>
            <a-tab-pane key="template" :tab="$t('whatsapp_business_api.metaInsightsTabTemplate')">
                <TemplateAnalytics
                    :channel="channel"
                    :filters="filters"
                    :loading="loadingStates.template"
                    :data="analyticsData.template"
                    :templates="templates"
                    :selectedTemplateIds="selectedTemplateIds"
                    @update:selectedTemplateIds="onTemplateIdsChange"
                    @fetch="fetchTemplateAnalytics"
                />
            </a-tab-pane>
            <a-tab-pane key="templateGroup" :tab="$t('whatsapp_business_api.metaInsightsTabTemplateGroup')">
                <TemplateGroupAnalytics
                    :channel="channel"
                    :filters="filters"
                    :loading="loadingStates.templateGroup"
                    :data="analyticsData.templateGroup"
                    :selectedGroupIds="selectedTemplateGroupIds"
                    @update:selectedGroupIds="onTemplateGroupIdsChange"
                    @fetch="fetchTemplateGroupAnalytics"
                />
            </a-tab-pane>
        </a-tabs>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import WHATSAPP_API from "../../../../../api/whatsapp";
import MessagingAnalytics from "./MessagingAnalytics.vue";
import ConversationAnalytics from "./ConversationAnalytics.vue";
import PricingAnalytics from "./PricingAnalytics.vue";
import TemplateAnalytics from "./TemplateAnalytics.vue";
import TemplateGroupAnalytics from "./TemplateGroupAnalytics.vue";

export default Vue.extend({
    name: "MetaInsights",
    components: {
        MessagingAnalytics,
        ConversationAnalytics,
        PricingAnalytics,
        TemplateAnalytics,
        TemplateGroupAnalytics,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            activeTab: "messaging",
            dateRange: [moment().subtract(30, "days"), moment()],
            dateFormat: "DD/MM/YYYY",
            granularity: "DAY",
            loading: false,
            loadingStates: {
                messaging: false,
                conversation: false,
                pricing: false,
                template: false,
                templateGroup: false,
            },
            analyticsData: {
                messaging: null,
                conversation: null,
                pricing: null,
                template: null,
                templateGroup: null,
            },
            templates: [],
            selectedTemplateIds: [],
            selectedTemplateGroupIds: [],
        };
    },
    computed: {
        filters() {
            return {
                start: this.dateRange[0] ? this.dateRange[0].unix() : moment().subtract(30, "days").unix(),
                end: this.dateRange[1] ? this.dateRange[1].unix() : moment().unix(),
                granularity: this.granularity,
            };
        },
    },
    methods: {
        onDateChange() {
            this.fetchCurrentTabData();
        },
        onGranularityChange() {
            this.fetchCurrentTabData();
        },
        onTabChange(key) {
            this.activeTab = key;
            this.fetchCurrentTabData();
        },
        async fetchCurrentTabData() {
            const tab = this.activeTab;
            if (tab === "messaging") {
                await this.fetchMessagingAnalytics();
            } else if (tab === "conversation") {
                await this.fetchConversationAnalytics();
            } else if (tab === "pricing") {
                await this.fetchPricingAnalytics();
            } else if (tab === "template") {
                await this.fetchTemplateAnalytics();
            } else if (tab === "templateGroup") {
                await this.fetchTemplateGroupAnalytics();
            }
        },
        async fetchAllData() {
            this.loading = true;
            await this.fetchCurrentTabData();
            this.loading = false;
        },
        async fetchMessagingAnalytics() {
            this.loadingStates.messaging = true;
            try {
                const params = {
                    start: this.filters.start,
                    end: this.filters.end,
                    granularity: this.filters.granularity,
                };
                const data = await WHATSAPP_API.INSIGHTS.getMessagingAnalytics(this.channel._id, params);
                this.analyticsData.messaging = data;
            } catch (error) {
                console.error("fetchMessagingAnalytics error:", error);
                this.$message.error(this.$t("whatsapp_business_api.metaInsightsErrorMessaging"));
            } finally {
                this.loadingStates.messaging = false;
            }
        },
        async fetchConversationAnalytics() {
            this.loadingStates.conversation = true;
            try {
                const granularity = this.filters.granularity === "DAY" ? "DAILY" : 
                                   this.filters.granularity === "MONTH" ? "MONTHLY" : this.filters.granularity;
                const params = {
                    start: this.filters.start,
                    end: this.filters.end,
                    granularity: granularity,
                    dimensions: JSON.stringify(["CONVERSATION_CATEGORY", "CONVERSATION_TYPE", "COUNTRY"]),
                };
                const data = await WHATSAPP_API.INSIGHTS.getConversationAnalytics(this.channel._id, params);
                this.analyticsData.conversation = data;
            } catch (error) {
                console.error("fetchConversationAnalytics error:", error);
                this.$message.error(this.$t("whatsapp_business_api.metaInsightsErrorConversation"));
            } finally {
                this.loadingStates.conversation = false;
            }
        },
        async fetchPricingAnalytics() {
            this.loadingStates.pricing = true;
            try {
                const granularity = this.filters.granularity === "DAY" ? "DAILY" : 
                                   this.filters.granularity === "MONTH" ? "MONTHLY" : this.filters.granularity;
                const params = {
                    start: this.filters.start,
                    end: this.filters.end,
                    granularity: granularity,
                    dimensions: JSON.stringify(["PRICING_CATEGORY", "PRICING_TYPE", "COUNTRY"]),
                };
                const data = await WHATSAPP_API.INSIGHTS.getPricingAnalytics(this.channel._id, params);
                this.analyticsData.pricing = data;
            } catch (error) {
                console.error("fetchPricingAnalytics error:", error);
                this.$message.error(this.$t("whatsapp_business_api.metaInsightsErrorPricing"));
            } finally {
                this.loadingStates.pricing = false;
            }
        },
        async fetchTemplateAnalytics() {
            if (this.selectedTemplateIds.length === 0) {
                this.analyticsData.template = null;
                return;
            }
            this.loadingStates.template = true;
            try {
                const params = {
                    start: this.dateRange[0] ? this.dateRange[0].format("YYYY-MM-DD") : moment().subtract(30, "days").format("YYYY-MM-DD"),
                    end: this.dateRange[1] ? this.dateRange[1].format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                    granularity: "DAILY",
                    template_ids: JSON.stringify(this.selectedTemplateIds.slice(0, 10)),
                };
                const data = await WHATSAPP_API.INSIGHTS.getTemplateAnalytics(this.channel._id, params);
                this.analyticsData.template = data;
            } catch (error) {
                console.error("fetchTemplateAnalytics error:", error);
                this.$message.error(this.$t("whatsapp_business_api.metaInsightsErrorTemplate"));
            } finally {
                this.loadingStates.template = false;
            }
        },
        async fetchTemplateGroupAnalytics() {
            if (this.selectedTemplateGroupIds.length === 0) {
                this.analyticsData.templateGroup = null;
                return;
            }
            this.loadingStates.templateGroup = true;
            try {
                const params = {
                    start: this.dateRange[0] ? this.dateRange[0].format("YYYY-MM-DD") : moment().subtract(30, "days").format("YYYY-MM-DD"),
                    end: this.dateRange[1] ? this.dateRange[1].format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                    granularity: "daily",
                    template_group_ids: JSON.stringify(this.selectedTemplateGroupIds.slice(0, 10)),
                };
                const data = await WHATSAPP_API.INSIGHTS.getTemplateGroupAnalytics(this.channel._id, params);
                this.analyticsData.templateGroup = data;
            } catch (error) {
                console.error("fetchTemplateGroupAnalytics error:", error);
                this.$message.error(this.$t("whatsapp_business_api.metaInsightsErrorTemplateGroup"));
            } finally {
                this.loadingStates.templateGroup = false;
            }
        },
        async fetchTemplates() {
            try {
                const response = await WHATSAPP_API.BUSINESS.getTemplates(this.channel._id);
                this.templates = _.get(response, "data", []) || [];
            } catch (error) {
                console.error("fetchTemplates error:", error);
            }
        },
        onTemplateIdsChange(ids) {
            this.selectedTemplateIds = ids;
        },
        onTemplateGroupIdsChange(ids) {
            this.selectedTemplateGroupIds = ids;
        },
    },
    mounted() {
        this.fetchMessagingAnalytics();
        this.fetchTemplates();
    },
});
</script>

<style scoped lang="scss">
.meta-insights {
    padding: 20px;
    background: var(--color-bg-default);
    min-height: calc(100vh - 200px);

    .header {
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 20px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .subtitle {
            font-size: 14px;
            color: var(--color-text-default);
            margin-top: 4px;
        }
    }

    .filters {
        display: flex;
        gap: 20px;
        align-items: flex-end;
        margin-bottom: 20px;
        padding: 15px;
        background: var(--color-bg-subtle);
        border-radius: 8px;

        .filter-item {
            display: flex;
            flex-direction: column;
            gap: 6px;

            label {
                font-size: 12px;
                font-weight: 600;
                color: var(--color-text-default);
            }
        }
    }

    ::v-deep .ant-tabs-nav-wrap {
        margin-bottom: 10px;
    }
}
</style>
